/**
 * PreUploadModal — Pre-Upload Description & Reviewer Tagging
 *
 * Opens after the user selects a file (before AI classification).
 * Collects:
 *   1. A one-word file type description (e.g. "Wireframe", "Mockup", "Contract")
 *   2. Optional reviewer tags from the project team
 *
 * On "Send" it calls onConfirm({ description, taggedReviewers }) which
 * triggers the AI classify-upload flow in TaskTabs.
 */
import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, Tag } from 'antd';
import { CloudUploadOutlined, TagOutlined, UserOutlined } from '@ant-design/icons';
import { formatBytes } from '../../utils/attachmentUtils';

const DESCRIPTION_SUGGESTIONS = [
    'Wireframe',
    'Mockup',
    'Contract',
    'Logo',
    'Proposal',
    'Invoice',
    'Report',
    'Deliverable',
    'Revision',
    'Brief',
    'Resource',
    'Reference',
    'Specification',
    'Screenshot',
    'Recording',
];

/**
 * Get a basic icon name for the file type pill preview.
 */
function getFileIcon(file) {
    if (!file) return 'attach_file';
    const mime = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    if (mime.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|svg)$/.test(name)) return 'image';
    if (mime.includes('pdf') || name.endsWith('.pdf')) return 'picture_as_pdf';
    if (mime.includes('video') || /\.(mp4|mov|avi|mkv)$/.test(name)) return 'videocam';
    if (mime.includes('audio') || /\.(mp3|wav|m4a)$/.test(name)) return 'audio_file';
    if (mime.includes('zip') || /\.(zip|rar|7z|tar|gz)$/.test(name)) return 'folder_zip';
    if (/\.(doc|docx|odt)$/.test(name)) return 'description';
    if (/\.(xls|xlsx|csv)$/.test(name)) return 'table_chart';
    return 'attach_file';
}

export default function PreUploadModal({ open, file, employees = [], onConfirm, onCancel }) {
    const [description, setDescription] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const [taggedReviewers, setTaggedReviewers] = useState([]);

    // Reset state each time the modal opens with a new file
    useEffect(() => {
        if (open) {
            setDescription('');
            setDescriptionError('');
            setTaggedReviewers([]);
        }
    }, [open, file?.name]);

    if (!file) return null;

    const handleDescriptionChange = (val) => {
        // Strip spaces — enforce one-word, max 50 chars, no special chars
        const cleaned = val.replace(/\s+/g, '').replace(/[^a-zA-Z0-9-_]/g, '').slice(0, 50);
        setDescription(cleaned);
        if (cleaned.length > 0) setDescriptionError('');
    };

    const handleSend = () => {
        const trimmed = description.trim();
        if (!trimmed) {
            setDescriptionError('Please enter a one-word description before sending.');
            return;
        }
        onConfirm({ description: trimmed, taggedReviewers });
    };

    const employeeOptions = employees
        .filter((e) => e && (e._id || e.id))
        .map((e) => {
            const fullName = [e.name, e.surname].filter(Boolean).join(' ') || e.fullName || e.email || 'Team Member';
            const value = String(e.id || e._id);
            return {
                value,
                label: (
                    <div className="flex items-center justify-between gap-2 py-0.5">
                        <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                {fullName.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">{fullName}</span>
                        </div>
                        {e.department && (
                            <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                                {e.department}
                            </span>
                        )}
                    </div>
                ),
                searchLabel: `${fullName} ${e.email || ''} ${e.department || ''}`.toLowerCase(),
                cleanName: fullName,
            };
        });

    const fileIcon = getFileIcon(file);

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            centered
            width={520}
            title={
                <div className="flex items-center gap-3 text-slate-800 dark:text-white font-bold text-base">
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                        <CloudUploadOutlined style={{ fontSize: 18 }} />
                    </span>
                    <span>Describe Your Upload</span>
                </div>
            }
        >
            <div className="space-y-5 py-2">
                {/* File preview pill */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-[#1a2538] border border-slate-200 dark:border-[#2a3852] rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                        <span className="material-symbols-outlined text-blue-600 dark:text-blue-400" style={{ fontSize: 22 }}>
                            {fileIcon}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 dark:text-white truncate" title={file.name}>
                            {file.name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {formatBytes(file.size)}
                        </p>
                    </div>
                </div>

                {/* One-word description */}
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">
                        <TagOutlined className="mr-1.5" />
                        File Type Description <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                        One word that describes this file (e.g. Wireframe, Mockup, Contract).
                    </p>

                    {/* Quick-pick chips */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {DESCRIPTION_SUGGESTIONS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => handleDescriptionChange(s)}
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${description === s
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                        : 'bg-white dark:bg-[#1a2538] text-slate-600 dark:text-slate-300 border-slate-200 dark:border-[#2a3852] hover:border-blue-400 hover:text-blue-600'
                                    }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>

                    {/* Custom input */}
                    <input
                        type="text"
                        value={description}
                        onChange={(e) => handleDescriptionChange(e.target.value)}
                        placeholder="Or type a custom one-word label…"
                        maxLength={50}
                        className={`w-full border rounded-xl px-3 py-2 text-sm bg-white dark:bg-[#141414] dark:text-white outline-none transition-all
                            ${descriptionError
                                ? 'border-red-400 focus:ring-1 focus:ring-red-400'
                                : 'border-slate-200 dark:border-[#303030] focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                    />
                    {descriptionError && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">error</span>
                            {descriptionError}
                        </p>
                    )}
                    {description && !descriptionError && (
                        <p className="text-xs text-slate-400 mt-1">
                            Label: <Tag color="blue" className="font-bold text-xs">{description}</Tag>
                        </p>
                    )}
                </div>

                {/* Tag reviewers */}
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">
                        <UserOutlined className="mr-1.5" />
                        Tag Reviewers <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                        Notify relevant team members to review this file.
                    </p>
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="Select team members to tag…"
                        value={taggedReviewers}
                        onChange={setTaggedReviewers}
                        options={employeeOptions}
                        filterOption={(input, option) =>
                            (option?.searchLabel || '').includes((input || '').toLowerCase())
                        }
                        className="w-full"
                        maxTagCount="responsive"
                    />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
                    <Button
                        onClick={onCancel}
                        className="text-sm font-semibold rounded-lg h-9"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSend}
                        icon={<CloudUploadOutlined />}
                        disabled={!description.trim()}
                        className="text-sm font-bold rounded-lg h-9 bg-blue-600 hover:bg-blue-500 shadow-sm disabled:opacity-50"
                    >
                        Send to AI Organizer
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
